import { useSelector } from 'react-redux';
import { WorksheetEquipmentDropdown, WorksheetGridFieldSection, WorksheetRecordGrid } from '../../Core';
import { Field, NumericInput, TextOutput, Checkbox } from '../../../Core';
import { ListFieldSection } from '../../../Layout';
import { withInjectedProps, NamePrefixModifier } from '../../../Core/InjectedPropsContext';
import deepEquals from '../../../../Lib/Object/deepEquals';
import { WorksheetRootComponent } from 'Components/Worksheets/Core/types';
import ids from '../../QestIds';
import { WorksheetBalanceFieldSet } from '../../Core/WorksheetEquipment';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import { hasValue, valueOrDefault } from 'Lib/Any';

const qestIds = [ids.oversizeDetermination.AS_1289_5_1_1];

let MeasuredOversizeDeterminationWorksheet: WorksheetRootComponent = ({ createWorksheetSelector }) => {
  /**
   * On the API we pass the sieve size properties ("AllSieves") separate from
   * the sieving records ("Sieves"). We need to merge these to determine properties
   * needed to display each sieve record.
   */
  const sieveRecordProps: Array<{
    caption?: string;
    sieveSizeQueryParams?: { SieveSize: string };
  }> = useSelector(
    combine([createWorksheetSelector!('Sieves'), createWorksheetSelector!('AllSieves')], (s, sieveData) =>
      s
        .map((s: any) => sieveData.find((d: any) => d.Id === s.SieveId))
        .map((sd: any) => ({
          caption: sd?.Caption,
          sieveSizeQueryParams: { SieveSize: `${sd?.Size.value} ${sd?.Size.uom}` },
        }))
    ),
    deepEquals
  );

  const disableNoOversizeMaterial = useSelector(
    pipe(createWorksheetSelector!('Sieves'), (s) => s?.some((s: any) => hasValue(s.WetMassRetained)) ?? false)
  );

  const noOversizeMaterial = useSelector(pipe(createWorksheetSelector!('NoOversizeMaterial'), valueOrDefault(false)));

  return (
    <>
      <ListFieldSection>
        <WorksheetBalanceFieldSet name="Equipment.Balance" />
        <NamePrefixModifier appendNamePrefix={'TotalMaterial'}>
          <Field input={<NumericInput />} name="TareMass" />
          <Field input={<NumericInput />} name="WetSoilAndTareMass" />
          <Field input={<TextOutput />} name="WetSoilMass" />
        </NamePrefixModifier>
      </ListFieldSection>

      <ListFieldSection>
        <Field input={<Checkbox />} name="NoOversizeMaterial" disabled={disableNoOversizeMaterial} />
      </ListFieldSection>

      {!noOversizeMaterial && (
        <WorksheetGridFieldSection visible={!noOversizeMaterial} name="Sieves">
          <WorksheetRecordGrid
            name={'Sieves'}
            orientation={'vertical'}
            tabDirection={'vertical'}
            recordProps={{
              headingLabel: 'Sieve Size',
              heading: (i) => sieveRecordProps[i]?.caption,
              preventRemove: true,
              preventCreate: true,
            }}
            fieldSet={[
              {
                name: 'Sieve',
                label: 'Sieve ID',
                component: (i) => (
                  <Field
                    input={<WorksheetEquipmentDropdown />}
                    equipmentType="sieve"
                    queryParams={sieveRecordProps[i]?.sieveSizeQueryParams}
                  />
                ),
                width: 200,
              },
              {
                name: 'WetMassRetained',
                component: <Field input={<NumericInput />} />,
                width: 130,
              },
              {
                name: 'CumulativeWetMassRetainedPercent',
                component: <Field input={<TextOutput />} />,
              },
            ]}
          />
        </WorksheetGridFieldSection>
      )}
    </>
  );
};

MeasuredOversizeDeterminationWorksheet = withInjectedProps(MeasuredOversizeDeterminationWorksheet);

MeasuredOversizeDeterminationWorksheet.qestIds = qestIds;

export default MeasuredOversizeDeterminationWorksheet;
export { MeasuredOversizeDeterminationWorksheet };
